/**
 * Central navigation configuration.
 *
 * All nav items live here. The sidebar reads this config and filters sections
 * at render-time based on the current user's roles and organisation plan.
 *
 * To add a new nav item: add it to BASE_NAV_SECTIONS with the appropriate
 * `roles` and `plans` constraints. No other file needs to change.
 */

import { IconType } from "react-icons";
import {
  FaHome,
  FaBriefcase,
  FaUsers,
  FaClipboardList,
  FaCreditCard,
  FaBuilding,
  FaUser,
  FaCog,
  FaFileWord,
  FaFolderOpen,
  FaLink,
  FaWallet,
  FaEnvelope,
  FaStore,
  FaComments,
  FaMoneyBillWave,
  FaUserCog,
  FaUserTie,
  FaCheckDouble,
  FaGlobeAfrica,
  FaFileSignature,
  FaExchangeAlt,
  FaHistory,
} from "react-icons/fa";
import { AppRole } from "@/modules/auth/constants/app-role";
import { PlanType } from "@/modules/organizations/types/plan-type";
import { IncreasePermission } from "@/modules/salary-increases/constants/increase-permissions";

// ── Access presets ─────────────────────────────────────────────────────────
// Single source of truth for plan/role constraints.
// Used by both nav filtering (buildNavSections) and route guards (App.tsx).

// ── Types ──────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string;
  icon: IconType;
  path?: string;
  submenu?: NavItem[];
  /** If set, the item is only shown when the user holds this permission */
  permission?: string;
}

export interface NavSection {
  title?: string;
  /** Small icon rendered next to the section title */
  icon?: IconType;
  items: NavItem[];
  /** If set, section is only shown when the org plan is in this list */
  plans?: PlanType[];
  /** If set, section is only shown when the user has at least one of these roles */
  roles?: AppRole[];
}

// ── Base config (static) ───────────────────────────────────────────────────

const BASE_NAV_SECTIONS: NavSection[] = [
  // ── Overview (org roles) ─────────────────────────────────────────────
  {
    roles: [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ],
    items: [{ label: "Overview", icon: FaHome, path: "/dashboard" }],
  },

  // ── Recruitment ──────────────────────────────────────────────────────
  {
    title: "Recruitment",
    icon: FaBriefcase,
    roles: [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ],
    plans: [PlanType.Recruitment, PlanType.FullHiring],
    items: [
      {
        label: "Jobs",
        icon: FaBriefcase,
        submenu: [
          {
            label: "Vacancies",
            icon: FaBriefcase,
            path: "/recruitment/vacancies",
          },
          {
            label: "Candidates",
            icon: FaUsers,
            path: "/recruitment/candidates",
          },
        ],
      },
    ],
  },

  // ── Onboarding ───────────────────────────────────────────────────────
  {
    title: "Onboarding",
    icon: FaClipboardList,
    roles: [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ],
    plans: [PlanType.Onboarding, PlanType.FullHiring],
    items: [
      {
        label: "Employees",
        icon: FaUsers,
        submenu: [
          {
            label: "All Employees",
            icon: FaUsers,
            path: "/onboarding/employees",
          },
          {
            label: "Offboarding",
            icon: FaUsers,
            path: "/onboarding/offboarding",
          },
        ],
      },
      {
        label: "Workflows",
        icon: FaClipboardList,
        submenu: [
          {
            label: "Workflow Manager",
            icon: FaClipboardList,
            path: "/onboarding/workflows",
          },
          {
            label: "Live Workflows",
            icon: FaClipboardList,
            path: "/onboarding/workflows/live",
          },
        ],
      },
    ],
  },

  // ── Documents (admin only) ───────────────────────────────────────────
  {
    title: "Documents",
    icon: FaFolderOpen,
    roles: [AppRole.OrganizationOwner, AppRole.SuperAdmin],
    items: [
      {
        label: "All Documents",
        icon: FaFolderOpen,
        path: "/admin/documents",
      },
    ],
  },

  // ── Employee ─────────────────────────────────────────────────────────
  {
    title: "Onboarding",
    icon: FaFileWord,
    roles: [AppRole.Employee],
    items: [
      { label: "Dashboard", icon: FaHome, path: "/employee/dashboard" },
      {
        label: "Onboarding",
        icon: FaClipboardList,
        path: "/employee/onboarding",
      },
      {
        label: "Offboarding",
        icon: FaBriefcase,
        path: "/employee/offboarding",
      },
      {
        label: "Documents",
        icon: FaFileWord,
        path: "/employee/documents",
      },
    ],
  },
];

// ── Marketplace plugin pages ─────────────────────────────────────────────────
// In-app pages shipped by marketplace plugins, keyed by plugin familyId. A nav
// item appears only when the org has an active install in that family, so the
// page surfaces exactly when the plugin is installed and activated.
const PLUGIN_NAV_ITEMS: Record<string, NavItem> = {
  engage: { label: "Engage", icon: FaComments, path: "/engage" },
  "salary-increase": {
    label: "Salary Increases",
    icon: FaMoneyBillWave,
    submenu: [
      {
        label: "Overview",
        icon: FaMoneyBillWave,
        path: "/salary-increases",
        permission: IncreasePermission.Read,
      },
      {
        label: "Reporting Line Cleanup",
        icon: FaUserCog,
        path: "/salary-increases/reporting-lines",
        permission: IncreasePermission.ManageReportingLines,
      },
      {
        label: "My Allocations",
        icon: FaUserTie,
        path: "/salary-increases/allocate",
        permission: IncreasePermission.Allocate,
      },
      {
        label: "BU Approval",
        icon: FaCheckDouble,
        path: "/salary-increases/bu-approval",
        permission: IncreasePermission.Approve,
      },
      {
        label: "Cross-BU Review",
        icon: FaGlobeAfrica,
        path: "/salary-increases/cross-bu-review",
        permission: IncreasePermission.ApproveFinal,
      },
      {
        label: "Final Approval",
        icon: FaFileSignature,
        path: "/salary-increases/final-approval",
        permission: IncreasePermission.ApproveFinal,
      },
      {
        label: "Sage 300 Submission",
        icon: FaExchangeAlt,
        path: "/salary-increases/sage-submission",
        permission: IncreasePermission.SubmitToSage,
      },
      {
        label: "Audit Trail",
        icon: FaHistory,
        path: "/salary-increases/audit-trail",
        permission: IncreasePermission.Read,
      },
    ],
  },
};

// ── Builder ────────────────────────────────────────────────────────────────

export interface NavBuildOptions {
  currentPlan?: PlanType | string;
  currentOrgId?: string | null;
  userRoles: AppRole[];
  /** Permissions the user holds in the current organisation (gates NavItem.permission). */
  userPermissions?: string[];
  /** familyIds of the org's active plugin installs (drives PLUGIN_NAV_ITEMS). */
  activePluginFamilies?: string[];
  /** Registered User as the organisation role: add-ons and the user's own profile only. */
  moduleAccessOnly?: boolean;
}

// Every user holds Registered User globally, so only the role held in this organisation says whether the
// user has the platform's pages or just its add-ons.
export function isModuleAccessOnly(orgRoles: AppRole[]): boolean {
  return (
    orgRoles.includes(AppRole.RegisteredUser) &&
    !orgRoles.includes(AppRole.OrganizationOwner) &&
    !orgRoles.includes(AppRole.TeamMember)
  );
}

export function firstNavPath(sections: NavSection[]): string | undefined {
  for (const section of sections) {
    for (const item of section.items) {
      const path = item.path ?? item.submenu?.find((child) => child.path)?.path;
      if (path) return path;
    }
  }
  return undefined;
}

/**
 * Returns the filtered, fully-resolved nav sections for the current user.
 * Call this once per render cycle (in the sidebar component).
 */
export function buildNavSections(options: NavBuildOptions): NavSection[] {
  const {
    currentPlan,
    currentOrgId,
    userRoles,
    userPermissions = [],
    activePluginFamilies,
    moduleAccessOnly = false,
  } = options;

  const filterByPermission = (item: NavItem): NavItem | null => {
    if (item.permission && !userPermissions.includes(item.permission)) {
      return null;
    }
    if (!item.submenu) return item;
    const submenu = item.submenu
      .map(filterByPermission)
      .filter((child): child is NavItem => child !== null);
    return submenu.length > 0 ? { ...item, submenu } : null;
  };

  const sections: NavSection[] = (moduleAccessOnly ? [] : BASE_NAV_SECTIONS)
    // Filter by role
    .filter((s) => !s.roles || s.roles.some((r) => userRoles.includes(r)))
    // Filter by plan
    .filter(
      (s) =>
        !s.plans ||
        (!!currentPlan && s.plans.includes(currentPlan as PlanType)),
    );

  // Settings section: dynamic because org-profile path depends on currentOrgId.
  // Shown only for org roles.
  const isOrgRole = userRoles.some((r) =>
    [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ].includes(r),
  );

  if (isOrgRole) {
    // Installed marketplace plugins that ship a page get their own section,
    // shown only while the plugin is active for this org.
    const pluginItems: NavItem[] = (activePluginFamilies ?? [])
      .map((familyId) => PLUGIN_NAV_ITEMS[familyId])
      .filter((item): item is NavItem => Boolean(item))
      .map(filterByPermission)
      .filter((item): item is NavItem => item !== null);

    if (pluginItems.length > 0) {
      sections.push({ title: "Add-ons", icon: FaStore, items: pluginItems });
    }

    const settingsItems: NavItem[] = [];

    if (currentOrgId) {
      const isOwner = userRoles.includes(AppRole.OrganizationOwner);

      if (isOwner) {
        settingsItems.push(
          {
            label: "Organisation Profile",
            icon: FaBuilding,
            path: `/account/profile/organization/${currentOrgId}`,
          },
          {
            label: "User Management",
            icon: FaUsers,
            path: "/settings/users",
          },
        );
      }
    }

    settingsItems.push(
      {
        label: "Billing & Subscriptions",
        icon: FaCreditCard,
        path: "/billing",
      },
      { label: "Wallet", icon: FaWallet, path: "/wallet" },
      { label: "Marketplace", icon: FaStore, path: "/marketplace" },
      { label: "Integrations", icon: FaLink, path: "/integrations" },
      {
        label: "Email Templates",
        icon: FaEnvelope,
        path: "/settings/email-templates",
      },
      { label: "My Profile", icon: FaUser, path: "/account/profile" },
    );

    sections.push({
      title: "Settings",
      icon: FaCog,
      items: moduleAccessOnly
        ? settingsItems.filter((item) => item.path === "/account/profile")
        : settingsItems,
    });
  }

  return sections;
}